/**
 * İPUCU YERLEŞİMİ — saf hesap, DOM'a dokunmaz (referans 02 · Dört yerleşim).
 *
 * Kural referanstan birebir: varsayılan ÜST, tercih sırası üst → alt → sağ →
 * sol. Bağlam başka bir yön isterse (referans 05-C: sağ kenardaki menüde
 * ipucu SOLA açılır) o yön öne alınır, kalan sıra değişmez. Kutu görünür
 * alana `edge` payıyla sığmıyorsa sıradaki yöne geçilir; çapraz eksende kutu
 * kaydırılır, ok tetikleyicinin ORTASINDA kalır. Hiçbir
 * yön sığmıyorsa (dar pencere) dikey eksende en geniş yer seçilir ve kutu
 * yine kenar payıyla sıkıştırılır.
 *
 * Bağımlılık eklenmedi (Floating UI dahil): dört yön ve bir kaydırma kuralı
 * için hesap bu kadardır.
 */

export type TooltipSide = 'top' | 'bottom' | 'right' | 'left';

export const TOOLTIP_SIDES: readonly TooltipSide[] = ['top', 'bottom', 'right', 'left'];

export interface PlacementRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface PlacementGeometry {
  /** Tetikleyici ile kutu arası. */
  readonly offset: number;
  /** Görünür alan kenarından asgari pay. */
  readonly edge: number;
  /** Okun kare kenarı; ok kutunun köşesine taşmasın diye kaydırma tavanı. */
  readonly arrow: number;
  /** Kutu köşesi; ok köşe yayının içine girmez. */
  readonly radius: number;
}

export interface Placement {
  readonly side: TooltipSide;
  /** Kutunun görünür alandaki sol üst köşesi (`position: fixed`). */
  readonly left: number;
  readonly top: number;
  /** Okun MERKEZİ — kutunun kendi ekseninde (üst/alt için x, sağ/sol için y). */
  readonly arrow: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function fits(
  side: TooltipSide,
  trigger: PlacementRect,
  tip: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
  g: PlacementGeometry,
): boolean {
  switch (side) {
    case 'top':
      return trigger.top - g.offset - tip.height >= g.edge;
    case 'bottom':
      return trigger.top + trigger.height + g.offset + tip.height <= viewport.height - g.edge;
    case 'right':
      return trigger.left + trigger.width + g.offset + tip.width <= viewport.width - g.edge;
    case 'left':
      return trigger.left - g.offset - tip.width >= g.edge;
  }
}

export function placeTooltip(
  trigger: PlacementRect,
  tip: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
  g: PlacementGeometry,
  preferred: TooltipSide = 'top',
): Placement {
  const fallback: TooltipSide =
    trigger.top >= viewport.height - (trigger.top + trigger.height) ? 'top' : 'bottom';
  const order = [preferred, ...TOOLTIP_SIDES.filter((candidate) => candidate !== preferred)];
  const side = order.find((candidate) => fits(candidate, trigger, tip, viewport, g));
  const chosen = side ?? fallback;
  const centerX = trigger.left + trigger.width / 2;
  const centerY = trigger.top + trigger.height / 2;
  /* Ok köşe yayına girmez: merkezi kenardan en az köşe + yarım ok içeride. */
  const arrowInset = g.radius + g.arrow / 2;

  if (chosen === 'top' || chosen === 'bottom') {
    const left = clamp(centerX - tip.width / 2, g.edge, viewport.width - g.edge - tip.width);
    const top =
      chosen === 'top'
        ? trigger.top - g.offset - tip.height
        : trigger.top + trigger.height + g.offset;
    return {
      side: chosen,
      left,
      top: side ? top : clamp(top, g.edge, viewport.height - g.edge - tip.height),
      arrow: clamp(centerX - left, arrowInset, tip.width - arrowInset),
    };
  }
  const top = clamp(centerY - tip.height / 2, g.edge, viewport.height - g.edge - tip.height);
  const left =
    chosen === 'right'
      ? trigger.left + trigger.width + g.offset
      : trigger.left - g.offset - tip.width;
  return {
    side: chosen,
    left,
    top,
    arrow: clamp(centerY - top, arrowInset, tip.height - arrowInset),
  };
}
